import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import { NetworkConfig, isStellarNetwork } from '../config/networks';
import { useWallet } from '../contexts/WalletContext';
import {
  fetchStellarRegistryOracleKeys,
  readStellarValidationConfig,
  submitStellarValidationConfiguration,
} from '../services/stellar';
import { createEvmReadProvider } from '../utils/evmProvider';
import CopyableId from './CopyableId';
import WalletSelector from './WalletSelector';
import './wizard/Step5_1OnChainConfig.css';

const VALIDATION_ENGINE_ABI = [
  'function trustlineOracleEnabled() view returns (bool)',
  'function sanctionsOracleEnabled() view returns (bool)',
  'function sanctionsOracle() view returns (address)',
  'function validationRegistry() view returns (address)',
  'function setValidationConfiguration(bool trustlineEnabled, bool sanctionsEnabled, string memory sanctionsOracleKey) external',
] as const;

const VALIDATOR_REGISTRY_ABI = [
  'function getAllRecords() view returns (string[] keys, address[] addrs)',
] as const;

const SANCTIONS_ONLY_VALIDATION_ENGINE_ABI = [
  'function setValidationConfiguration(bool sanctionsEnabled, address sanctionsOracle_) external',
] as const;

const DAPP_VE_ABI = ['function validationEngine() view returns (address)'] as const;

const ORACLE_KEY_SUFFIX = '_ORACLE';

export interface OnChainConfigPanelProps {
  chain: NetworkConfig;
  /** Known VE address (Stellar inspection, or EVM resolved via RPC by the parent). */
  validationEngineAddress?: string | null;
  /** EVM dApp contract — used to resolve VE via public RPC when address is not provided. */
  contractAddress?: string;
  /** Derived from whether any method has a policy mapping. */
  desiredTrustline: boolean;
  compact?: boolean;
}

const OnChainConfigPanel: React.FC<OnChainConfigPanelProps> = ({
  chain,
  validationEngineAddress,
  contractAddress,
  desiredTrustline,
  compact = false,
}) => {
  const {
    signer,
    isConnected,
    currentProvider,
    account,
    walletKind,
    connectFreighter,
    connectWalletWithProvider,
    signStellarTransaction,
  } = useWallet();

  const stellar = isStellarNetwork(chain);

  const [resolvedVeAddress, setResolvedVeAddress] = useState<string | null>(
    validationEngineAddress || null
  );
  const [veResolveError, setVeResolveError] = useState<string | null>(null);
  const [isResolvingVe, setIsResolvingVe] = useState(false);

  const [onChainTrustline, setOnChainTrustline] = useState<boolean | null>(null);
  const [onChainSanctions, setOnChainSanctions] = useState<boolean | null>(null);
  const [onChainSanctionsOracleAddress, setOnChainSanctionsOracleAddress] = useState<string | null>(
    null
  );
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [sanctionsOracleKeys, setSanctionsOracleKeys] = useState<string[]>([]);
  const [sanctionsOracleKeyToAddress, setSanctionsOracleKeyToAddress] = useState<
    Record<string, string>
  >({});
  const [oracleKeysFetchError, setOracleKeysFetchError] = useState<string | null>(null);
  const [isFetchingOracleKeys, setIsFetchingOracleKeys] = useState(false);
  const [isSubmittingTx, setIsSubmittingTx] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);
  const [txSuccess, setTxSuccess] = useState<string | null>(null);

  const [desiredSanctions, setDesiredSanctions] = useState<boolean | null>(null);
  const [sanctionsOracleKey, setSanctionsOracleKey] = useState<string | null>(null);
  const [hydratedFromChain, setHydratedFromChain] = useState(false);
  const [walletSelectorOpen, setWalletSelectorOpen] = useState(false);

  const proxyAddress = stellar
    ? validationEngineAddress || resolvedVeAddress
    : resolvedVeAddress || validationEngineAddress;

  const walletReady = stellar
    ? isConnected && walletKind === 'stellar' && !!account
    : isConnected && walletKind === 'evm' && !!signer;

  const wrongWalletKind =
    isConnected &&
    ((stellar && walletKind !== 'stellar') || (!stellar && walletKind !== 'evm'));

  const ensureWalletOnSelectedNetwork = useCallback(async (): Promise<void> => {
    if (!currentProvider || !chain) {
      throw new Error('No wallet provider or target chain available');
    }
    const selectedChainHex = `0x${chain.chainId.toString(16)}`;
    const currentChainHex = await currentProvider.request({ method: 'eth_chainId' });
    if ((currentChainHex || '').toLowerCase() === selectedChainHex.toLowerCase()) return;

    try {
      await currentProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: selectedChainHex }],
      });
    } catch (switchErr: any) {
      if (switchErr?.code === 4902) {
        await currentProvider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: selectedChainHex,
              chainName: chain.name,
              rpcUrls: [chain.rpcUrl],
              blockExplorerUrls: [chain.blockExplorer],
              nativeCurrency: chain.nativeCurrency,
            },
          ],
        });
      } else {
        throw switchErr;
      }
    }
  }, [currentProvider, chain]);

  // Seed / reset VE from props
  useEffect(() => {
    if (stellar && validationEngineAddress) {
      setResolvedVeAddress(validationEngineAddress);
      setVeResolveError(null);
      setIsResolvingVe(false);
    }
    if (!stellar) {
      setResolvedVeAddress(validationEngineAddress || null);
      if (validationEngineAddress) {
        setVeResolveError(null);
        setIsResolvingVe(false);
      }
    }
  }, [stellar, validationEngineAddress]);

  // EVM: resolve VE via public RPC when address not provided — no wallet required
  useEffect(() => {
    if (stellar) return;
    if (validationEngineAddress) return;
    if (!contractAddress) {
      setResolvedVeAddress(null);
      setVeResolveError(null);
      setIsResolvingVe(false);
      return;
    }

    let cancelled = false;
    const resolve = async () => {
      setIsResolvingVe(true);
      setVeResolveError(null);
      try {
        const provider = createEvmReadProvider(chain);
        const contract = new ethers.Contract(contractAddress, DAPP_VE_ABI, provider);
        const ve = await contract.validationEngine();
        if (cancelled) return;
        if (ve && ve !== ethers.ZeroAddress) {
          setResolvedVeAddress(ethers.getAddress(ve));
        } else {
          setResolvedVeAddress(null);
          setVeResolveError('No validation engine found on this contract.');
        }
      } catch (err: any) {
        if (cancelled) return;
        console.warn('Could not resolve validation engine via RPC:', err);
        setResolvedVeAddress(null);
        setVeResolveError(err?.shortMessage || err?.message || 'Failed to resolve validation engine');
      } finally {
        if (!cancelled) setIsResolvingVe(false);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [stellar, contractAddress, validationEngineAddress, chain]);

  const fetchOnChainState = useCallback(
    async (silent = false) => {
      if (!proxyAddress) return;
      if (!silent) {
        setIsFetching(true);
        setFetchError(null);
      }
      try {
        if (isStellarNetwork(chain)) {
          const state = await readStellarValidationConfig(chain, proxyAddress);
          setOnChainTrustline(state.trustlineEnabled);
          setOnChainSanctions(state.sanctionsEnabled);
          setOnChainSanctionsOracleAddress(null);
          return;
        }

        const provider = createEvmReadProvider(chain);
        const contract = new ethers.Contract(proxyAddress, VALIDATION_ENGINE_ABI, provider);
        const [trustline, sanctions, sanctionsOracleAddr] = await Promise.all([
          contract.trustlineOracleEnabled(),
          contract.sanctionsOracleEnabled(),
          contract.sanctionsOracle(),
        ]);
        setOnChainTrustline(Boolean(trustline));
        setOnChainSanctions(Boolean(sanctions));
        setOnChainSanctionsOracleAddress(
          sanctionsOracleAddr && sanctionsOracleAddr !== ethers.ZeroAddress
            ? ethers.getAddress(sanctionsOracleAddr)
            : null
        );
      } catch (err: any) {
        if (!silent) {
          console.error('Error fetching on-chain config:', err);
          setFetchError(err?.shortMessage || err?.message || 'Failed to read on-chain configuration');
          setOnChainTrustline(null);
          setOnChainSanctions(null);
          setOnChainSanctionsOracleAddress(null);
        }
      } finally {
        if (!silent) setIsFetching(false);
      }
    },
    [proxyAddress, chain]
  );

  const fetchOracleKeys = useCallback(async () => {
    if (!proxyAddress) return;
    setIsFetchingOracleKeys(true);
    setOracleKeysFetchError(null);
    try {
      if (isStellarNetwork(chain)) {
        const { keys, keyToAddress } = await fetchStellarRegistryOracleKeys(chain, proxyAddress);
        setSanctionsOracleKeys(keys);
        setSanctionsOracleKeyToAddress(keyToAddress);
        return;
      }

      const provider = createEvmReadProvider(chain);
      const engineContract = new ethers.Contract(proxyAddress, VALIDATION_ENGINE_ABI, provider);
      const registryAddress = await engineContract.validationRegistry();
      if (!registryAddress || registryAddress === ethers.ZeroAddress) {
        setOracleKeysFetchError('No validation registry on this engine');
        setSanctionsOracleKeys([]);
        return;
      }
      const registryContract = new ethers.Contract(registryAddress, VALIDATOR_REGISTRY_ABI, provider);
      const [keys, addrs] = await registryContract.getAllRecords();
      const keyList = keys as string[];
      const addrList = addrs as string[];
      const oracleKeys: string[] = [];
      const keyToAddr: Record<string, string> = {};
      keyList.forEach((k: string, i: number) => {
        if (typeof k === 'string' && k.endsWith(ORACLE_KEY_SUFFIX) && addrList[i]) {
          oracleKeys.push(k);
          keyToAddr[k] = ethers.getAddress(addrList[i]);
        }
      });
      setSanctionsOracleKeys(oracleKeys);
      setSanctionsOracleKeyToAddress(keyToAddr);
    } catch (err: any) {
      console.error('Error fetching oracle keys:', err);
      setOracleKeysFetchError(err?.shortMessage || err?.message || 'Failed to load sanctions oracle keys');
      setSanctionsOracleKeys([]);
      setSanctionsOracleKeyToAddress({});
    } finally {
      setIsFetchingOracleKeys(false);
    }
  }, [proxyAddress, chain]);

  useEffect(() => {
    setHydratedFromChain(false);
    setDesiredSanctions(null);
    setSanctionsOracleKey(null);
    setOnChainTrustline(null);
    setOnChainSanctions(null);
    if (!proxyAddress) return;
    fetchOnChainState(false);
  }, [fetchOnChainState, proxyAddress]);

  useEffect(() => {
    if (!proxyAddress) return;
    fetchOracleKeys();
  }, [proxyAddress, fetchOracleKeys]);

  useEffect(() => {
    if (!proxyAddress) return;
    const interval = setInterval(() => fetchOnChainState(true), 5000);
    return () => clearInterval(interval);
  }, [proxyAddress, fetchOnChainState]);

  useEffect(() => {
    if (hydratedFromChain) return;
    if (onChainTrustline === null || onChainSanctions === null) return;
    if (isFetchingOracleKeys) return;

    setDesiredSanctions(onChainSanctions);
    if (onChainSanctions && onChainSanctionsOracleAddress) {
      const match = Object.entries(sanctionsOracleKeyToAddress).find(
        ([, addr]) => addr.toLowerCase() === onChainSanctionsOracleAddress.toLowerCase()
      );
      setSanctionsOracleKey(match?.[0] || null);
    } else {
      setSanctionsOracleKey(null);
    }
    setHydratedFromChain(true);
  }, [
    hydratedFromChain,
    onChainTrustline,
    onChainSanctions,
    onChainSanctionsOracleAddress,
    sanctionsOracleKeyToAddress,
    isFetchingOracleKeys,
  ]);

  const noOraclesAvailable =
    !isFetchingOracleKeys && !oracleKeysFetchError && sanctionsOracleKeys.length === 0;

  useEffect(() => {
    if (noOraclesAvailable && desiredSanctions === true) {
      setDesiredSanctions(false);
      setSanctionsOracleKey(null);
    }
  }, [noOraclesAvailable, desiredSanctions]);

  const configMatches = useMemo(() => {
    if (onChainTrustline === null || onChainSanctions === null || desiredSanctions === null) {
      return false;
    }
    if (onChainTrustline !== desiredTrustline || onChainSanctions !== desiredSanctions) {
      return false;
    }
    if (!desiredSanctions) return true;
    if (stellar) return onChainSanctions === true;
    const selectedKey = sanctionsOracleKey?.trim();
    if (!selectedKey) return false;
    const expectedAddr = sanctionsOracleKeyToAddress[selectedKey];
    if (!expectedAddr || !onChainSanctionsOracleAddress) return false;
    try {
      return ethers.getAddress(expectedAddr) === ethers.getAddress(onChainSanctionsOracleAddress);
    } catch {
      return expectedAddr.toLowerCase() === onChainSanctionsOracleAddress.toLowerCase();
    }
  }, [
    onChainTrustline,
    onChainSanctions,
    onChainSanctionsOracleAddress,
    desiredTrustline,
    desiredSanctions,
    sanctionsOracleKey,
    sanctionsOracleKeyToAddress,
    stellar,
  ]);

  const sanctionsKeyForTx =
    desiredSanctions && sanctionsOracleKey?.trim() ? sanctionsOracleKey.trim() : '';
  const sanctionsOracleAddressForTx = sanctionsKeyForTx
    ? sanctionsOracleKeyToAddress[sanctionsKeyForTx]
    : '';

  const canSubmitTx =
    walletReady &&
    !!proxyAddress &&
    !!chain &&
    desiredSanctions !== null &&
    (desiredSanctions === false ||
      (desiredSanctions === true &&
        sanctionsKeyForTx !== '' &&
        (stellar || !!sanctionsOracleAddressForTx)));

  const handleWalletSelect = async (provider: any, providerName: string) => {
    try {
      if (provider?.__stellar || providerName === 'Freighter') {
        await connectFreighter();
      } else {
        await connectWalletWithProvider(provider, providerName);
      }
      setWalletSelectorOpen(false);
    } catch (err) {
      console.error('Failed to connect wallet:', err);
    }
  };

  const openWalletConnect = () => {
    if (stellar) {
      connectFreighter().catch(() => setWalletSelectorOpen(true));
    } else {
      setWalletSelectorOpen(true);
    }
  };

  const handleSubmitTransaction = async () => {
    if (!proxyAddress || desiredSanctions === null || !chain) return;
    if (desiredSanctions && !sanctionsKeyForTx) return;
    if (desiredSanctions && !stellar && !sanctionsOracleAddressForTx) return;

    setIsSubmittingTx(true);
    setTxError(null);
    setTxSuccess(null);

    try {
      if (stellar) {
        if (walletKind !== 'stellar' || !account) {
          throw new Error('Connect Freighter to submit the Stellar configuration transaction');
        }
        await submitStellarValidationConfiguration({
          chain,
          validationEngineAddress: proxyAddress,
          publicKey: account,
          signTransaction: signStellarTransaction,
          trustlineEnabled: desiredTrustline,
          sanctionsEnabled: desiredSanctions,
          sanctionsKey: sanctionsKeyForTx || null,
        });
        await fetchOnChainState(false);
        setTxSuccess('On-chain configuration updated.');
        return;
      }

      if (!signer) return;
      await ensureWalletOnSelectedNetwork();
      const runtimeSigner = currentProvider
        ? await new ethers.BrowserProvider(currentProvider).getSigner()
        : signer;

      const contract = new ethers.Contract(proxyAddress, VALIDATION_ENGINE_ABI, runtimeSigner);
      const tx = await contract.setValidationConfiguration(
        desiredTrustline,
        desiredSanctions,
        sanctionsKeyForTx
      );
      await tx.wait();
      await fetchOnChainState(false);
      setTxSuccess('On-chain configuration updated.');
    } catch (primaryErr: any) {
      if (stellar) {
        console.error('Stellar set_validation_configuration failed:', primaryErr);
        setTxError(primaryErr?.message || 'Configuration transaction failed');
        return;
      }

      console.warn('Primary setValidationConfiguration failed, trying fallback:', primaryErr);
      try {
        await ensureWalletOnSelectedNetwork();
        const runtimeSigner = currentProvider
          ? await new ethers.BrowserProvider(currentProvider).getSigner()
          : signer;
        const fallbackOracleAddress = desiredSanctions
          ? sanctionsOracleAddressForTx
          : ethers.ZeroAddress;
        const fallbackContract = new ethers.Contract(
          proxyAddress,
          SANCTIONS_ONLY_VALIDATION_ENGINE_ABI,
          runtimeSigner
        );
        const fallbackTx = await fallbackContract.setValidationConfiguration(
          desiredSanctions,
          fallbackOracleAddress
        );
        await fallbackTx.wait();
        await fetchOnChainState(false);
        setTxSuccess('On-chain configuration updated.');
      } catch (fallbackErr: any) {
        console.error('Both primary and fallback transactions failed:', primaryErr, fallbackErr);
        const networkIssue =
          primaryErr?.code === 4902 ||
          fallbackErr?.code === 4902 ||
          primaryErr?.code === 4001 ||
          fallbackErr?.code === 4001 ||
          (primaryErr?.message && primaryErr.message.toLowerCase().includes('network')) ||
          (fallbackErr?.message && fallbackErr.message.toLowerCase().includes('network'));
        if (networkIssue) {
          setTxError(`Wrong network. Please switch your wallet to ${chain.name}.`);
        } else {
          const primaryMessage = primaryErr?.reason || primaryErr?.message || 'Primary method failed';
          const fallbackMessage =
            fallbackErr?.reason || fallbackErr?.message || 'Fallback method failed';
          setTxError(
            `Configuration transaction failed. Primary: ${primaryMessage}. Fallback: ${fallbackMessage}`
          );
        }
      }
    } finally {
      setIsSubmittingTx(false);
    }
  };

  const submitWalletCallout = (!walletReady || wrongWalletKind) && (
    compact ? (
      <div className="dash-onchain-callout">
        <p>
          {stellar
            ? wrongWalletKind
              ? 'An EVM wallet is connected. Connect Freighter for this Stellar dApp.'
              : 'Connect Freighter to submit the Stellar configuration transaction.'
            : wrongWalletKind
              ? 'A Stellar wallet is connected. Connect an EVM wallet to submit the configuration transaction.'
              : `Connect an EVM wallet on ${chain.name} to submit the configuration transaction.`}
        </p>
        <button type="button" className="dash-btn dash-btn-secondary" onClick={openWalletConnect}>
          {stellar ? 'Connect Freighter' : 'Connect wallet'}
        </button>
      </div>
    ) : (
      <div className="step5-1-tx-warning">
        <p>
          {stellar
            ? wrongWalletKind
              ? 'An EVM wallet is connected. Connect Freighter for this Stellar dApp.'
              : 'Connect Freighter to submit the Stellar configuration transaction.'
            : wrongWalletKind
              ? 'A Stellar wallet is connected. Connect an EVM wallet to submit the configuration transaction.'
              : `Connect an EVM wallet on ${chain.name} to submit the configuration transaction.`}
        </p>
        <button type="button" onClick={openWalletConnect} className="step5-1-retry-btn">
          {stellar ? 'Connect Freighter' : 'Connect wallet'}
        </button>
      </div>
    )
  );

  if (compact) {
    return (
      <div className="dash-onchain">
        <p className="dash-onchain-intro">
          {stellar ? (
            <>
              Settings are read from your Validation Engine. Freighter is only required to
              submit changes.
            </>
          ) : (
            <>
              Settings are read from your Validation Engine on{' '}
              <strong>{chain.name}</strong>. A wallet is only required to submit changes.
            </>
          )}
        </p>
        {proxyAddress && (
          <div className="dash-address-row">
            <span className="dash-address-row-label">Validation Engine</span>
            <CopyableId value={proxyAddress} label="Validation Engine" />
          </div>
        )}

        {veResolveError && <div className="dash-error">{veResolveError}</div>}

        {!proxyAddress && !veResolveError && (isResolvingVe || (!stellar && !!contractAddress)) && (
          <p className="dash-onchain-hint">Resolving validation engine…</p>
        )}

        {proxyAddress && (
          <>
            <div className="dash-onchain-option">
              <div className="dash-onchain-option-header">
                <span className="dash-onchain-option-label">Trustline Oracle validation</span>
                <span className={`dash-badge ${desiredTrustline ? 'dash-badge-on' : 'dash-badge-off'}`}>
                  {desiredTrustline ? 'Enabled' : 'Disabled'}
                </span>
                {onChainTrustline !== null && onChainTrustline !== desiredTrustline && (
                  <span className="dash-badge dash-badge-off">
                    On-chain: {onChainTrustline ? 'On' : 'Off'}
                  </span>
                )}
              </div>
              <p className="dash-onchain-option-desc">
                Enabled when at least one contract method has a policy mapped. Turns on Trustline
                validation for methods that use{' '}
                <code>{stellar ? 'require_trustline()' : 'requireTrustline()'}</code>.
              </p>
            </div>

            <div className="dash-onchain-option">
              <div className="dash-onchain-option-header">
                <span className="dash-onchain-option-label">OFAC sanctions check</span>
                {noOraclesAvailable && (
                  <span className="dash-badge dash-badge-off">Disabled</span>
                )}
              </div>
              <p className="dash-onchain-option-desc">
                When enabled, the caller and related addresses are checked against the OFAC
                sanctions list when available.
              </p>
              {noOraclesAvailable ? (
                <p className="dash-onchain-option-desc">
                  This chain does not support sanctions oracles. No oracle keys are available in
                  the registry.
                </p>
              ) : (
                <>
                  <div className="dash-onchain-radio-group">
                    <label className="dash-onchain-radio-label">
                      <input
                        type="radio"
                        name="dash-sanctions"
                        checked={desiredSanctions === true}
                        onChange={() => {
                          setDesiredSanctions(true);
                          setTxError(null);
                          setTxSuccess(null);
                        }}
                        disabled={!hydratedFromChain}
                      />
                      Enable
                    </label>
                    <label className="dash-onchain-radio-label">
                      <input
                        type="radio"
                        name="dash-sanctions"
                        checked={desiredSanctions === false}
                        onChange={() => {
                          setDesiredSanctions(false);
                          setSanctionsOracleKey(null);
                          setTxError(null);
                          setTxSuccess(null);
                        }}
                        disabled={!hydratedFromChain}
                      />
                      Disable
                    </label>
                  </div>
                  {desiredSanctions === true && (
                    <div className="dash-form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="dash-sanctions-oracle-key">Sanctions oracle</label>
                      {isFetchingOracleKeys ? (
                        <p className="dash-onchain-hint">Loading oracle keys from registry…</p>
                      ) : oracleKeysFetchError ? (
                        <div className="dash-error">{oracleKeysFetchError}</div>
                      ) : (
                        <select
                          id="dash-sanctions-oracle-key"
                          value={sanctionsOracleKey ?? ''}
                          onChange={(e) => {
                            setSanctionsOracleKey(e.target.value || null);
                            setTxError(null);
                            setTxSuccess(null);
                          }}
                        >
                          <option value="">Select a sanctions oracle…</option>
                          {sanctionsOracleKeys.map((key) => (
                            <option key={key} value={key}>
                              {key}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="dash-onchain-status">
              {isFetching ? (
                <p className="dash-onchain-hint">Reading on-chain configuration…</p>
              ) : fetchError ? (
                <div className="dash-error" style={{ display: 'flex', gap: '0.65rem', alignItems: 'center' }}>
                  <span style={{ flex: 1 }}>{fetchError}</span>
                  <button
                    type="button"
                    className="dash-btn dash-btn-secondary"
                    onClick={() => fetchOnChainState(false)}
                  >
                    Retry
                  </button>
                </div>
              ) : onChainTrustline !== null && onChainSanctions !== null ? (
                desiredSanctions === null ? (
                  <p className="dash-onchain-hint">Loading current configuration…</p>
                ) : desiredSanctions === true &&
                  !sanctionsKeyForTx &&
                  !noOraclesAvailable &&
                  !stellar ? (
                  <p className="dash-onchain-hint">Select a sanctions oracle above to continue.</p>
                ) : desiredSanctions === true &&
                  !sanctionsKeyForTx &&
                  stellar &&
                  !configMatches ? (
                  <p className="dash-onchain-hint">
                    Select a sanctions oracle to update the on-chain configuration.
                  </p>
                ) : configMatches ? (
                  <div className="dash-success" style={{ marginBottom: 0 }}>
                    Configuration is up to date on-chain.
                  </div>
                ) : (
                  <>
                    <p className="dash-onchain-option-desc">
                      On-chain state differs from the desired configuration. Submit a transaction
                      to apply it.
                    </p>

                    {submitWalletCallout}

                    {txError && <div className="dash-error">{txError}</div>}
                    {txSuccess && <div className="dash-success">{txSuccess}</div>}

                    <div className="dash-actions">
                      <button
                        type="button"
                        className="dash-btn dash-btn-primary"
                        onClick={handleSubmitTransaction}
                        disabled={!canSubmitTx || isSubmittingTx}
                      >
                        {isSubmittingTx ? 'Submitting…' : 'Submit transaction'}
                      </button>
                    </div>
                  </>
                )
              ) : null}
            </div>
          </>
        )}

        <WalletSelector
          isOpen={walletSelectorOpen}
          onClose={() => setWalletSelectorOpen(false)}
          onSelectWallet={handleWalletSelect}
        />
      </div>
    );
  }

  return (
    <div className="wizard-step step5-1-onchain-config">
      <h2 className="wizard-step-title">On-Chain Behaviour Configuration</h2>
      <p className="wizard-step-description">
        Trustline includes on-chain settings that live on your Validation Engine
        {stellar ? '' : ' Proxy'}. Applying this configuration requires a signed transaction.
      </p>

      {veResolveError && (
        <div className="step5-1-onchain-error" style={{ marginBottom: '1rem' }}>
          <p>{veResolveError}</p>
        </div>
      )}

      {!proxyAddress && !veResolveError && (isResolvingVe || (!stellar && !!contractAddress)) && (
        <p className="step5-1-onchain-status" style={{ marginBottom: '1rem' }}>
          Resolving validation engine…
        </p>
      )}

      {proxyAddress && (
        <div className="step5-1-config-block">
          <h3 className="step5-1-config-title">Configuration options</h3>

          <div className="step5-1-option step5-1-option-readonly">
            <div className="step5-1-option-header">
              <span className="step5-1-option-label">Trustline Oracle validation</span>
              <span
                className={`step5-1-option-badge ${desiredTrustline ? 'step5-1-badge-on' : 'step5-1-badge-off'}`}
              >
                {desiredTrustline ? 'Enabled' : 'Disabled'}
              </span>
              {onChainTrustline !== null && onChainTrustline !== desiredTrustline && (
                <span className="step5-1-option-badge step5-1-badge-off">
                  On-chain: {onChainTrustline ? 'On' : 'Off'}
                </span>
              )}
            </div>
            <p className="step5-1-option-desc">
              Enabled when at least one contract method has a policy mapped. This turns on Trustline
              validation for methods that use{' '}
              <code>{stellar ? 'require_trustline()' : 'requireTrustline()'}</code>.
            </p>
          </div>

          <div className="step5-1-option">
            <div className="step5-1-option-header">
              <span className="step5-1-option-label">OFAC sanctions check</span>
              {noOraclesAvailable && (
                <span className="step5-1-option-badge step5-1-badge-off">Disabled</span>
              )}
            </div>
            <p className="step5-1-option-desc">
              When enabled, the caller and related addresses are checked against the OFAC sanctions
              list when available.
            </p>
            {noOraclesAvailable ? (
              <p className="step5-1-option-desc step5-1-forced-disabled-text">
                This chain does not support sanctions oracles. No oracle keys are available in the
                registry.
              </p>
            ) : (
              <>
                <div className="step5-1-radio-group">
                  <label className="step5-1-radio-label">
                    <input
                      type="radio"
                      name="dash-sanctions-wizard"
                      checked={desiredSanctions === true}
                      onChange={() => {
                        setDesiredSanctions(true);
                        setTxError(null);
                        setTxSuccess(null);
                      }}
                      disabled={!hydratedFromChain}
                    />
                    <span className="step5-1-radio-text">Enable</span>
                  </label>
                  <label className="step5-1-radio-label">
                    <input
                      type="radio"
                      name="dash-sanctions-wizard"
                      checked={desiredSanctions === false}
                      onChange={() => {
                        setDesiredSanctions(false);
                        setSanctionsOracleKey(null);
                        setTxError(null);
                        setTxSuccess(null);
                      }}
                      disabled={!hydratedFromChain}
                    />
                    <span className="step5-1-radio-text">Disable</span>
                  </label>
                </div>
                {desiredSanctions === true && (
                  <div className="step5-1-oracle-key-group">
                    <label htmlFor="dash-sanctions-oracle-key-w" className="step5-1-option-label">
                      Sanctions oracle
                    </label>
                    {isFetchingOracleKeys ? (
                      <p className="step5-1-option-desc">Loading oracle keys from registry…</p>
                    ) : oracleKeysFetchError ? (
                      <p className="wizard-error">{oracleKeysFetchError}</p>
                    ) : (
                      <select
                        id="dash-sanctions-oracle-key-w"
                        value={sanctionsOracleKey ?? ''}
                        onChange={(e) => {
                          setSanctionsOracleKey(e.target.value || null);
                          setTxError(null);
                          setTxSuccess(null);
                        }}
                        className="step5-1-oracle-key-select"
                      >
                        <option value="">Select a sanctions oracle…</option>
                        {sanctionsOracleKeys.map((key) => (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="step5-1-config-status">
            {isFetching ? (
              <p className="step5-1-onchain-status">Reading on-chain configuration…</p>
            ) : fetchError ? (
              <div className="step5-1-onchain-error">
                <p>{fetchError}</p>
                <button type="button" onClick={() => fetchOnChainState(false)} className="step5-1-retry-btn">
                  Retry
                </button>
              </div>
            ) : onChainTrustline !== null && onChainSanctions !== null ? (
              desiredSanctions === null ? (
                <p className="step5-1-onchain-hint">Loading current configuration…</p>
              ) : desiredSanctions === true && !sanctionsKeyForTx && !noOraclesAvailable && !stellar ? (
                <p className="step5-1-onchain-hint">Select a sanctions oracle above to continue.</p>
              ) : desiredSanctions === true && !sanctionsKeyForTx && stellar && !configMatches ? (
                <p className="step5-1-onchain-hint">
                  Select a sanctions oracle to update the on-chain configuration.
                </p>
              ) : configMatches ? (
                <div className="step5-1-success-block">
                  <div className="step5-1-success-icon">✓</div>
                  <p className="step5-1-success-message">Configuration is up to date on-chain.</p>
                </div>
              ) : (
                <>
                  <p className="step5-1-option-desc">
                    On-chain state differs from the desired configuration. Submit a transaction to
                    apply it.
                  </p>

                  {submitWalletCallout}

                  {txError && <div className="wizard-error step5-1-tx-error">{txError}</div>}
                  {txSuccess && (
                    <div className="step5-1-success-block" style={{ marginBottom: '0.75rem' }}>
                      <p className="step5-1-success-message">{txSuccess}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmitTransaction}
                    disabled={!canSubmitTx || isSubmittingTx}
                    className="step5-1-submit-tx-btn"
                  >
                    {isSubmittingTx ? 'Submitting…' : 'Submit transaction'}
                  </button>
                </>
              )
            ) : null}
          </div>
        </div>
      )}

      <WalletSelector
        isOpen={walletSelectorOpen}
        onClose={() => setWalletSelectorOpen(false)}
        onSelectWallet={handleWalletSelect}
      />
    </div>
  );
};

export default OnChainConfigPanel;
